import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, MessageCircle, Lock } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { WHATSAPP_URL } from "../../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", business: "", phone: "", city: "", email: "" };

export default function LeadForm() {
  const { t, lang } = useLang();
  const tr = t.lead;
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");

  const setField = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (values.name.trim().length < 2) e.name = tr.errors.required;
    if (values.business.trim().length < 2) e.business = tr.errors.required;
    if (!/^[+\d][\d\s\-()]{5,}$/.test(values.phone.trim())) e.phone = tr.errors.phoneInvalid;
    if (values.city.trim().length < 2) e.city = tr.errors.required;
    if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()))
      e.email = tr.errors.emailInvalid;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        name: values.name.trim(),
        business: values.business.trim(),
        phone: values.phone.trim(),
        city: values.city.trim(),
        lang,
        source: "landing-form",
      };
      if (values.email.trim()) body.email = values.email.trim();
      await axios.post(`${API}/leads`, body);
      setSuccess(true);
      setValues(initial);
    } catch (err) {
      setServerError(tr.errors.network);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] glow-orange opacity-70" />
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center rounded-[2rem] p-7 md:p-12 bg-surface/70 border border-white/[0.07] backdrop-blur-sm"
        >
          {/* Left: Title */}
          <div>
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-brand-orange/90 font-medium">
              {tr.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-tight">
              {tr.title}
            </h2>
            <p className="mt-4 text-base md:text-lg text-white/65 leading-relaxed">
              {tr.subtitle}
            </p>
            <div className="mt-6 hidden md:block">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="lead-or-whatsapp"
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors group"
              >
                <MessageCircle size={16} className="text-[#25D366]" />
                <span className="underline-grow">{tr.orWhats}</span>
                <ArrowRight size={14} className="opacity-60 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right: Form / Success */}
          <div className="relative" data-testid="lead-form-wrapper">
            <AnimatePresence mode="wait">
              {!success ? (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  noValidate
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3.5"
                  data-testid="lead-form"
                >
                  <Field
                    id="name"
                    label={tr.fields.name}
                    placeholder={tr.placeholders.name}
                    value={values.name}
                    onChange={setField("name")}
                    error={errors.name}
                    autoComplete="name"
                  />
                  <Field
                    id="business"
                    label={tr.fields.business}
                    placeholder={tr.placeholders.business}
                    value={values.business}
                    onChange={setField("business")}
                    error={errors.business}
                    autoComplete="organization"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <Field
                      id="phone"
                      label={tr.fields.phone}
                      placeholder={tr.placeholders.phone}
                      value={values.phone}
                      onChange={setField("phone")}
                      error={errors.phone}
                      autoComplete="tel"
                      inputMode="tel"
                    />
                    <Field
                      id="city"
                      label={tr.fields.city}
                      placeholder={tr.placeholders.city}
                      value={values.city}
                      onChange={setField("city")}
                      error={errors.city}
                      autoComplete="address-level2"
                    />
                  </div>

                  <Field
                    id="email"
                    label={tr.fields.email}
                    placeholder={tr.placeholders.email}
                    value={values.email}
                    onChange={setField("email")}
                    error={errors.email}
                    autoComplete="email"
                    inputMode="email"
                    type="email"
                  />

                  {serverError && (
                    <p className="text-sm text-red-400" data-testid="lead-form-server-error">
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-form-submit"
                    className="group w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-full px-6 py-3.5 text-base font-medium transition-all shadow-[0_0_24px_rgba(249,115,22,0.4)] hover:shadow-[0_0_36px_rgba(249,115,22,0.6)]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {tr.submitting}
                      </>
                    ) : (
                      <>
                        {tr.submit}
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  <p className="flex items-center gap-1.5 text-xs text-white/40 pt-1">
                    <Lock size={11} />
                    {tr.privacy}
                  </p>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="rounded-2xl bg-emerald-500/10 border border-emerald-500/25 p-7 text-center"
                  data-testid="lead-form-success"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 size={26} className="text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {tr.success.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/65 max-w-sm mx-auto leading-relaxed">
                    {tr.success.body}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="lead-form-reset"
                    className="mt-5 text-sm text-brand-orange hover:text-brand-orangeHover transition-colors underline-grow"
                  >
                    {tr.success.again}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ id, label, error, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-white/55 mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <input
        id={id}
        name={id}
        data-testid={`lead-input-${id}`}
        {...rest}
        className={`w-full px-4 py-3 rounded-xl bg-white/[0.04] border ${
          error ? "border-red-400/60" : "border-white/10 focus:border-brand-orange/60"
        } text-white text-[15px] placeholder:text-white/30 outline-none focus:bg-white/[0.06] transition-colors`}
      />
      {error && (
        <p className="mt-1 text-xs text-red-400" data-testid={`lead-error-${id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
